import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { resolveBlogImageUrl } from '../utils/tenantAssetUrls';
import './Blog.css';

const PAGE_SIZE = 9;

function stripHtml(html) {
  return String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const tag = searchParams.get('tag') || '';

  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(PAGE_SIZE),
        });
        if (tag) params.set('tag', tag);

        const response = await api.get(`/blog?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        const data = await response.json();
        if (cancelled) return;
        setPosts(data.data || []);
        setPagination(data.pagination || { page, limit: PAGE_SIZE, total: 0, pages: 1 });
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching blog posts:', err);
        setError('Unable to load blog posts. Please try again later.');
        setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, [page, tag]);

  const goToPage = (nextPage) => {
    const params = {};
    if (nextPage > 1) params.page = String(nextPage);
    if (tag) params.tag = tag;
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const totalPages = Math.max(1, pagination.pages || 1);

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div className="blog-hero-image">
          <img 
            src="https://images.squarespace-cdn.com/content/v1/5cb69ab47a1fbd701676934b/1577170369127-IBXEQUXW5SK5ZLQULDAG/farm+mist+jpg.jpg" 
            alt="Farm at sunrise"
          />
        </div>
        <div className="blog-hero-content">
          <h1>Blog</h1>
          <p>Stories and updates from Hood Family Farms</p>
        </div>
      </section>

      <section className="blog-content section">
        <div className="container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="no-posts">
              <h2>No posts yet</h2>
              <p>Check back soon for updates from the farm!</p>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {posts.map(post => {
                  const excerpt = stripHtml(post.excerpt);
                  return (
                    <article key={post.id} className="blog-card">
                      <Link to={`/blog/${post.slug}`} className="blog-card-link">
                        <div className="blog-card-image">
                          <img 
                            src={resolveBlogImageUrl(post.featured_image) || 'https://images.squarespace-cdn.com/content/v1/5cb69ab47a1fbd701676934b/1555900163717-AYN5T9AUW61HX4WYDX8K/wide+shot+sunset.jpg'} 
                            alt={post.title} 
                          />
                        </div>
                        <div className="blog-card-content">
                          <span className="blog-date">{formatDate(post.published_at)}</span>
                          <h2>{post.title}</h2>
                          <p className="blog-excerpt">{excerpt || '\u00a0'}</p>
                          {post.tags && post.tags.length > 0 && (
                            <div className="blog-tags">
                              {post.tags.map((tagName, index) => (
                                <span key={index} className="blog-tag">{tagName}</span>
                              ))}
                            </div>
                          )}
                          <span className="read-more">Read More →</span>
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <nav className="blog-pagination" aria-label="Blog pages">
                  <button
                    type="button"
                    className="blog-page-btn"
                    disabled={page <= 1}
                    onClick={() => goToPage(page - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="blog-page-status">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="blog-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => goToPage(page + 1)}
                  >
                    Next →
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default Blog;
